import { Search } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSearch } from "../api";
import { artistNames } from "../format";
import { useDebounced } from "../hooks";

export function SearchBox({ initial = "", autoFocus = false, large = false }: { initial?: string; autoFocus?: boolean; large?: boolean }) {
  const [text, setText] = useState(initial);
  const [open, setOpen] = useState(false);
  const [active, setActiveState] = useState(-1);
  // Mirrors `active` synchronously, so Enter right after an arrow key never reads a stale index.
  const activeRef = useRef(-1);
  const setActive = (next: number | ((i: number) => number)) => {
    activeRef.current = typeof next === "function" ? next(activeRef.current) : next;
    setActiveState(activeRef.current);
  };
  const debounced = useDebounced(text.trim(), 200);
  const navigate = useNavigate();
  const listId = useId();
  const box = useRef<HTMLDivElement>(null);
  const { data } = useSearch(debounced, { limit: 6 }, open && debounced.length >= 2);

  useEffect(() => setText(initial), [initial]);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const options = [
    ...(data?.artists.slice(0, 3).map((a) => ({ key: `a${a.id}`, href: `/wykonawca/${a.id}`, label: a.name, hint: "wykonawca" })) ?? []),
    ...(data?.tracks.map((t) => ({ key: `t${t.id}`, href: `/utwor/${t.id}`, label: t.title, hint: artistNames(t.artists, t.credit) })) ?? []),
  ];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const pick = options[activeRef.current];
    setOpen(false);
    if (pick) navigate(pick.href);
    else if (text.trim()) navigate(`/szukaj?q=${encodeURIComponent(text.trim())}`);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, options.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, -1)); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className={`searchbox${large ? " searchbox--large" : ""}`} ref={box}>
      <form role="search" onSubmit={submit}>
        <label className="sr-only" htmlFor={`${listId}-input`}>Szukaj utworu lub wykonawcy</label>
        <div className="searchbox__field">
        <Search className="searchbox__icon icon" size={18} aria-hidden="true" />
        <input
          id={`${listId}-input`} type="search" value={text} autoFocus={autoFocus} autoComplete="off" spellCheck={false}
          placeholder="Szukaj utworu lub wykonawcy…" role="combobox" aria-expanded={open && options.length > 0}
          aria-controls={listId} aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
          onChange={(e) => { setText(e.target.value); setOpen(true); setActive(-1); }}
          onFocus={() => setOpen(true)} onKeyDown={onKey}
        />
        </div>
        <button type="submit" className="searchbox__submit"><span>Szukaj</span></button>
      </form>
      {open && options.length > 0 && (
        <ul className="suggest" id={listId} role="listbox">
          {options.map((o, i) => (
            <li key={o.key} id={`${listId}-${i}`} role="option" aria-selected={i === active}
              className={i === active ? "is-active" : ""} onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => { e.preventDefault(); setOpen(false); navigate(o.href); }}>
              <span>{o.label}</span><small>{o.hint}</small>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
